import React, { useMemo } from 'react';
import { Button, CircularProgress, FormControl, Grid, Icon, InputLabel, Select, TextField } from '@material-ui/core';
import { ContentArea } from '@styles/common';
import { usePagedData } from '@domain/hooks/use.paged.data';
import { Loading } from '@components/loading';
import { ErrorDisplay } from '@components/error/error.display';
import { CancelToken } from 'axios';
import { useQueryParam } from '@domain/hooks/use.query';
import PlayerListItem from '@screens/players/player.list.item';
import { PlayerData } from '@domain/types/player/player.data';
import MainPagination from '@components/main.pagination';
import { useAuth } from '@domain/auth/auth.hook';
import { decodeCid } from '@domain/utils/cid.utils';
import { createResponse } from '@domain/utils/data.utils';

const AUTO_HELPER_TEXT = '"Auto" will search by "PID" when typing only numbers, by "CID" if the format is found, and by "Name" otherwise';

interface PlayerListParams {
  field?: string;
  value?: string;
}

function requestCondition(data: PlayerListParams) {
  return !!data.value;
}

function paramMap(data: PlayerListParams) {
  return {
    f: data.field === 'auto' ? '' : data.field,
    s: data.value,
  };
}

const Players = () => {
  const { authorizedServices } = useAuth();

  async function runSearch(data: PlayerListParams, page: number, cancelToken: CancelToken | undefined) {
    let { field, value } = data;

    if (!value) return createResponse<PlayerData[]>();

    if (!field || field === 'auto') {
      let v = value?.trim() || '';

      // detect CID and convert to PID
      if (/[a-z]+\d+/i.test(v)) {
        v = decodeCid(v.toUpperCase()).toString();
        value = v;
      }

      field = /\d+/i.test(v) ? 'pid' : 'fullName';
    }

    return authorizedServices.get<PlayerData[]>('/admin/player/search', { params: { page, value, field }, cancelToken });
  }

  const searchField = useQueryParam('f', 'auto');
  const search = useQueryParam('s');
  const initialData = useMemo<PlayerListParams>(() => ({ field: searchField, value: search }), [searchField, search]);

  const formData = usePagedData(initialData, runSearch, {
    clearResponseOnRequest: true,
    requestCondition,
    paramMap,
  });

  const { data, response, loading, error, submit, handleChange, pageCount } = formData;
  const result = response?.data;

  return (
    <>
      <ContentArea>
        <Loading loading={loading} />

        <form onSubmit={submit}>
          <fieldset disabled={loading}>
            <Grid container spacing={2}>
              <Grid item>
                <FormControl>
                  <InputLabel id='field'>Field</InputLabel>
                  <Select
                    native
                    labelId='field'
                    id='field'
                    value={data.field}
                    inputProps={{ name: 'field' }}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value='auto'>Auto</option>
                    <option value='fullName'>Name</option>
                    <option value='pid'>PID</option>
                    <option value='phone'>Phone</option>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item style={{ flexGrow: 1 }}>
                <TextField
                  id='value'
                  name='value'
                  label='Type your search'
                  value={data.value}
                  onChange={handleChange}
                  disabled={loading}
                  fullWidth
                  helperText={data.field === 'auto' ? AUTO_HELPER_TEXT : ''}
                />
              </Grid>

              <Grid item style={{ paddingTop: 20 }}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} /> : <Icon>search</Icon>}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </fieldset>
        </form>
      </ContentArea>

      <ErrorDisplay error={error} />

      {result && (
        <ContentArea>
          {result.length === 0 && 'No players found.'}
          {result.map((player) => (
            <PlayerListItem key={player.pid} player={player} listItem />
          ))}
          {result.length > 0 && <MainPagination count={pageCount} />}
        </ContentArea>
      )}
    </>
  );
};

export default Players;
